import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAdmin } from "../../context/AdminContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Building2, CheckCircle, XCircle, Clock, ArrowLeft, RefreshCw, LogOut
} from "lucide-react";
import { API_BASE_URL } from "../../lib/config";

const API_URL = API_BASE_URL;

const AdminDistricts = () => {
  const { token, logout } = useAdmin();
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    fetchDistricts();
  }, []);

  const fetchDistricts = async () => {
    setLoading(true);
    try {
      // Reuse admin teachers endpoint and filter district_admin role
      const res = await axios.get(`${API_URL}/admin/teachers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const districtAdmins = (res.data?.teachers || res.data || []).filter((t) => t.role === "district_admin");
      setDistricts(districtAdmins);
    } catch {
      toast.error("Failed to load districts");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setActionLoading((prev) => ({ ...prev, [id]: "approving" }));
    try {
      await axios.put(`${API_URL}/admin/teachers/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("District approved!");
      fetchDistricts();
    } catch {
      toast.error("Failed to approve district");
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: null }));
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm("Reject this district registration?")) return;
    setActionLoading((prev) => ({ ...prev, [id]: "rejecting" }));
    try {
      await axios.put(`${API_URL}/admin/teachers/${id}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("District rejected.");
      fetchDistricts();
    } catch {
      toast.error("Failed to reject district");
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: null }));
    }
  };

  const statusBadge = (status) => {
    if (status === "approved")
      return <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold"><CheckCircle className="w-3 h-3" /> Approved</span>;
    if (status === "rejected")
      return <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold"><XCircle className="w-3 h-3" /> Rejected</span>;
    return <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold"><Clock className="w-3 h-3" /> Pending</span>;
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/dashboard" className="text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-purple-400" />
            <h1 className="font-heading font-bold text-lg">District Management</h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchDistricts}
            className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-colors"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            onClick={logout}
            className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-colors"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold">District Registrations</h2>
            <p className="text-slate-400 text-sm mt-1">{districts.length} district admin(s) total</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : districts.length === 0 ? (
          <div className="text-center py-20 text-slate-400">
            <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No district registrations yet.</p>
          </div>
        ) : (
          <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-700">
                <tr>
                  <th className="text-left px-6 py-4 text-slate-400 font-semibold">Admin</th>
                  <th className="text-left px-6 py-4 text-slate-400 font-semibold">District</th>
                  <th className="text-left px-6 py-4 text-slate-400 font-semibold">Registered</th>
                  <th className="text-center px-6 py-4 text-slate-400 font-semibold">Status</th>
                  <th className="text-center px-6 py-4 text-slate-400 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {districts.map((d) => (
                  <tr key={d.id} className="border-b border-slate-700/50 hover:bg-slate-750 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-semibold text-white">{d.name}</p>
                      <p className="text-slate-400 text-xs">{d.email}</p>
                    </td>
                    <td className="px-6 py-4 text-slate-300">{d.district_name || "—"}</td>
                    <td className="px-6 py-4 text-slate-400 text-xs">
                      {d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-6 py-4 text-center">{statusBadge(d.status)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        {d.status !== "approved" && (
                          <button
                            onClick={() => handleApprove(d.id)}
                            disabled={!!actionLoading[d.id]}
                            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
                          >
                            <CheckCircle className="w-3.5 h-3.5" />
                            {actionLoading[d.id] === "approving" ? "..." : "Approve"}
                          </button>
                        )}
                        {d.status !== "rejected" && (
                          <button
                            onClick={() => handleReject(d.id)}
                            disabled={!!actionLoading[d.id]}
                            className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
                          >
                            <XCircle className="w-3.5 h-3.5" />
                            {actionLoading[d.id] === "rejecting" ? "..." : "Reject"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDistricts;
